<template>
    <v-dialog v-model="editBundleDialog" max-width="700" persistent>
        <template v-slot:activator="{ on: dialog, attrs }">
            <slot name="dialogactivator" :on="dialog" :attrs="attrs">
                <div v-bind="attrs" v-on="dialog">
                    <v-btn icon small text color="secondary"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                </div>
            </slot>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <span class="secondary--text ml-1">Edit {{ bundleTypeName }}</span>
                <v-spacer></v-spacer>
                <v-btn :disabled="editingBundle" @click="editBundleDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert type="info" v-if="highlightHandBack" class="mt-6" text>
                    Enable handback visibility below for students to see their corrections and grades
                </v-alert>
                <v-row align="center">
                    <v-col cols="12" md="5"><span class="subtitle-2">Handback Visibility</span></v-col>
                    <v-col>
                        <v-radio-group v-model="showBundleHandbacks" row hide-details>
                            <v-radio label="Hidden" :value="false"></v-radio>
                            <v-radio label="Visible" :value="true"></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row align="center" v-if="!highlightHandBack">
                    <v-col cols="12" md="5"><span class="subtitle-2">Assignment description</span></v-col>
                    <v-col>
                        <v-textarea rows="2" v-model="bundleDescription" outlined hide-details></v-textarea>
                    </v-col>
                </v-row>
                <v-row align="center" v-if="!highlightHandBack">
                    <v-col cols="12" md="5"><span class="subtitle-2">Handin size limit</span></v-col>
                    <v-col>
                        <v-form v-model="formValid">
                            <v-text-field
                                v-model="handinLimit"
                                outlined
                                required
                                v-mask="integerMask"
                                :rules="[rules.required, rules.handinMinimum, rules.handinMaximum]">
                                <template v-slot:append-outer>
                                    <span class="mt-1">MB</span>
                                </template>
                            </v-text-field>
                        </v-form>
                    </v-col>
                </v-row>
                <v-row align="center" v-if="!highlightHandBack">
                    <v-col cols="12" md="5"><span class="subtitle-2">Hand-in deadline</span></v-col>
                    <v-col>
                        <div class="d-flex flex-column">
                            <v-menu v-model="bundleDateMenu" :close-on-content-click="false" transition="scale-transition" min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="bundleDeadlineDate" readonly label="Date" v-bind="attrs" v-on="on" outlined></v-text-field>
                                </template>
                                <v-date-picker v-model="bundleDeadlineDate" @input="bundleDateMenu = false"></v-date-picker>
                            </v-menu>
                            <v-menu v-model="bundleTimeMenu" :close-on-content-click="false" transition="scale-transition" max-width="310px" min-width="310px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="bundleDeadlineTime" label="Time" readonly v-bind="attrs" v-on="on" outlined></v-text-field>
                                </template>
                                <v-card>
                                    <v-card-text>
                                        <time-picker v-if="bundleTimeMenu" v-model="bundleDeadlineTime" format="24hr"></time-picker>
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                            <div v-if="bundleHandinDatetime" class="d-flex">
                                <span class="mr-1">Displayed deadline:</span>
                                <span class="font-weight-medium">{{ bundleHandinDatetime | dateTimeToHuman }}</span>
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-divider class="my-4" />
                <v-row dense>
                    <v-col cols="12">
                        <div class="mb-3">
                            Add a message to notify students of changes (e.g. due date updated, grades now visible) or select no notifications below
                        </div>
                        <v-text-field
                            :disabled="skipNotify"
                            hide-details
                            v-model="updateMessage"
                            placeholder="Please detail your changes here"
                            outlined></v-text-field>
                        <v-checkbox v-model="skipNotify" label="Do not notify students"></v-checkbox>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <div class="d-flex align-center justify-end w-100">
                    <v-btn :loading="editingBundle" :disabled="disableEditButton" @click="editBundle" text color="primary">
                        <span v-if="!skipNotify">Save & Notify</span>
                        <span v-else>Save</span>
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { makeShortId, capitalize } from '@/utils'
import TimePicker from '@/components/TimePicker.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const integerMask = createNumberMask({
    prefix: '',
    includeThousandsSeparator: false,
    integerLimit: 3
})

export default {
    name: 'BundleEditDialog',
    components: {
        TimePicker
    },
    props: {
        bundleData: Object,
        highlightHandBack: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editBundleDialog: false,
            editingBundle: false,
            bundleName: '',
            bundleDescription: '',
            bundleDeadlineTime: null,
            bundleTimeMenu: false,
            bundleDeadlineDate: null,
            bundleDateMenu: false,
            showBundleHandbacks: false,
            updateMessage: '',
            skipNotify: false,
            integerMask,
            handinLimit: 10,
            rules: {
                required: value => !!value || 'Required.',
                handinMinimum: value => {
                    const num = parseInt(value)
                    return (!isNaN(num) && num >= 1) || 'Minimum value is 1MB'
                },
                handinMaximum: value => {
                    const num = parseInt(value)
                    return (!isNaN(num) && num <= 100) || 'Maximum value is 100MB'
                }
            },
            formValid: false
        }
    },
    computed: {
        bundleTypeName() {
            return capitalize(this.bundleData?.bundle_type_name)
        },
        bundleHandinDatetime() {
            if (this.bundleDeadlineDate && this.bundleDeadlineTime) {
                const datetime = `${this.bundleDeadlineDate} ${this.bundleDeadlineTime}`
                const UTCDatetime = new Date(datetime).toISOString()
                return UTCDatetime
            }
            return ''
        },
        disableEditButton() {
            if (!this.formValid || !this.bundleName || !this.bundleDeadlineDate || !this.bundleDeadlineTime || (!this.updateMessage && !this.skipNotify)) {
                return true
            }
            return false
        }
    },
    mounted() {
        this.bundleName = this.bundleData.long_id
        this.bundleDescription = this.bundleData.description
        this.showBundleHandbacks = this.bundleData.handbacks_visible ? this.bundleData.handbacks_visible : false
        this.setPickerDateTime()
        this.handinLimit = this.bundleData.handin_limit_bytes / (1000 * 1000)
    },
    methods: {
        setPickerDateTime() {
            const localDateFromUTC = new Date(this.bundleData.allow_handins_until)
            const mm = localDateFromUTC.getMonth() + 1 // getMonth() is zero-based
            const dd = localDateFromUTC.getDate()
            const hh = localDateFromUTC.getHours()
            const min = localDateFromUTC.getMinutes()
            const pickerDate = [localDateFromUTC.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join('-')
            const pickerTime = [(hh > 9 ? '' : '0') + hh, (min > 9 ? '' : '0') + min].join(':')
            this.bundleDeadlineDate = pickerDate
            this.bundleDeadlineTime = pickerTime
        },
        editBundle() {
            this.editingBundle = true
            const patchBody = {}
            if (this.bundleName !== this.bundleData.long_id) {
                patchBody.long_id = this.bundleName
                patchBody.short_id = makeShortId(this.bundleName)
            }
            if (this.bundleDescription !== this.bundleData.description) {
                patchBody.description = this.bundleDescription
            }
            if (this.bundleHandinDatetime && this.bundleHandinDatetime !== this.bundleData.allow_handins_until) {
                patchBody.allow_handins_until = this.bundleHandinDatetime
            }
            if (this.showBundleHandbacks !== this.bundleData.handbacks_visible) {
                patchBody.handbacks_visible = this.showBundleHandbacks
            }
            if (this.updateMessage) {
                patchBody.message = this.updateMessage
            }
            patchBody.handin_limit_bytes = this.handinLimit * 1000 * 1000
            this.$axios
                .patch(`/bundles/${this.bundleData.bid}`, patchBody)
                .then(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: `Successfully edited ${this.bundleData.long_id}` + (this.updateMessage ? ' and students have been notified.' : ''),
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                    this.$store.dispatch('spaceStore/fetchSpaceBundles', this.$route.params.sid)
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: `Failed to edit ${this.bundleData.long_id}`,
                        snackBarTimeout: 10000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    this.editingBundle = false
                    this.editBundleDialog = false
                })
        }
    }
}
</script>
